const TICKET_GROUP_PREFIX = 'TicketGroup:'
const TICKET_ACCOUNT_PREFIX = 'TicketAccount:'

function workflowSlice(source, key) {
  const tree = source?.[key]
  if (Array.isArray(tree)) return tree
  if (tree && typeof tree === 'object' && Array.isArray(tree.values)) return tree.values
  return []
}

/**
 * Slim list-row settings tree from a full settings bundle or list payload
 * entry.
 *
 * @param {object|null|undefined} source
 * @returns {{ statuses: object[], priorities: object[], categories: object[] }|null}
 */
function slimFromSource(source) {
  if (!source || typeof source !== 'object') return null

  const root = source.settings && typeof source.settings === 'object' ? source.settings : source
  const statuses = workflowSlice(root, 'statuses')
  const priorities = workflowSlice(root, 'priorities')
  const categories = workflowSlice(root, 'categories')

  const hasWorkflow =
    (Array.isArray(statuses) && statuses.length > 0) ||
    (Array.isArray(priorities) && priorities.length > 0) ||
    (Array.isArray(categories) && categories.length > 0)

  if (!hasWorkflow) return null

  return {
    statuses: Array.isArray(statuses) ? statuses : [],
    priorities: Array.isArray(priorities) ? priorities : [],
    categories: Array.isArray(categories) ? categories : [],
  }
}

/**
 * Normalize one entry from a ticket list `settingsHosts` map.
 *
 * @param {object|null|undefined} host
 * @returns {{ statuses: object[], priorities: object[], categories: object[] }|null}
 */
function normalizeHost(host) {
  return slimFromSource(host)
}

function ticketGroupKey(groupId) {
  return groupId ? `${TICKET_GROUP_PREFIX}${groupId}` : null
}

function ticketAccountKey(accountId) {
  return accountId ? `${TICKET_ACCOUNT_PREFIX}${accountId}` : null
}

function normalizeGroupId(ref) {
  if (ref == null || ref === '') return null
  if (typeof ref === 'object') return ref._id != null ? String(ref._id) : null
  return String(ref)
}

function findTicketGroup(user, groupId) {
  const id = normalizeGroupId(groupId)
  if (!id) return null
  return (user?.ticketGroups ?? []).find((row) => String(row._id) === id) ?? null
}

function ticketGroupExists(user, groupId) {
  return Boolean(findTicketGroup(user, groupId))
}

function accountIdsInGroup(group) {
  return (group?.accounts ?? [])
    .map((id) => String(typeof id === 'object' ? id._id : id))
    .filter(Boolean)
}

function accountGroupId(user, accountId) {
  const id = accountId != null ? String(accountId) : ''
  if (!id || !user) return null

  const currentId = user.currentAccount?._id ?? user.currentAccount
  if (currentId != null && String(currentId) === id) {
    return normalizeGroupId(user.currentAccount?.ticketGroup)
  }

  const account = (user.accounts ?? []).find((row) => {
    const rowId = row && typeof row === 'object' ? row._id : row
    return rowId != null && String(rowId) === id
  })
  if (account && typeof account === 'object') return normalizeGroupId(account.ticketGroup)

  return null
}

/**
 * Resolve the ticket group an account belongs to. Session `ticketGroups`
 * membership wins; account `ticketGroup` refs fill gaps when the session
 * lags a save. Stale refs (account no longer listed on the group) return null.
 */
function findGroupForAccount(user, accountId) {
  const id = accountId != null ? String(accountId) : ''
  if (!id || !user) return null

  const fromSession = (user.ticketGroups ?? []).find((row) =>
    accountIdsInGroup(row).some((memberId) => memberId === id),
  )
  if (fromSession) return fromSession

  const linkedId = accountGroupId(user, id)
  if (!linkedId) return null

  const group = findTicketGroup(user, linkedId)
  if (group) {
    return accountIdsInGroup(group).some((memberId) => memberId === id) ? group : null
  }

  // Account still points at a group id that isn't in the session — stale ref.
  const knownGroupIds = new Set((user.ticketGroups ?? []).map((row) => String(row._id)))
  if (knownGroupIds.size > 0 && !knownGroupIds.has(linkedId)) return null

  // Session may lag a save; trust the account ref until the session catches up.
  return { _id: linkedId, accounts: [id] }
}

/**
 * When every account resolves to the same ticket group, return that group id.
 * Mixed groups, any ungrouped account, or an empty list → `null`.
 *
 * @param {object|null|undefined} user
 * @param {string[]} accountIds
 * @returns {string|null}
 */
function sharedGroupIdForAccounts(user, accountIds) {
  if (!user || !accountIds?.length) return null

  let sharedId = null
  for (const accountId of accountIds) {
    const group = findGroupForAccount(user, accountId)
    if (!group?._id) return null
    const groupId = String(group._id)
    if (sharedId == null) sharedId = groupId
    else if (sharedId !== groupId) return null
  }

  return sharedId
}

function resolveSettingsHostKey(ticket, user) {
  const accountId = ticket?.createdBy != null && ticket.createdBy !== '' ? String(ticket.createdBy) : ''

  if (accountId) {
    const group = findGroupForAccount(user, accountId)
    if (group?._id) return ticketGroupKey(String(group._id))
    return ticketAccountKey(accountId)
  }

  if (ticket?.settingsHostKey) {
    const key = String(ticket.settingsHostKey)
    if (key.startsWith(TICKET_GROUP_PREFIX)) {
      const groupId = key.slice(TICKET_GROUP_PREFIX.length)
      if (ticketGroupExists(user, groupId)) return key
      return null
    }
    return key
  }

  if (ticket?.ticketGroupId) {
    const groupId = String(ticket.ticketGroupId)
    if (ticketGroupExists(user, groupId)) return ticketGroupKey(groupId)
  }

  return null
}

/**
 * Resolve a settings host row from the list map by `settingsHostKey`.
 *
 * @param {Record<string, object>|null|undefined} map
 * @param {string|null|undefined} key
 * @returns {{ statuses: object[], priorities: object[], categories: object[] }|null}
 */
function resolveHost(map, key) {
  if (!key || !map) return null

  const direct = normalizeHost(map[key])
  if (direct) return direct

  const meta = map[key]
  if (meta?.onModel === 'TicketGroup' && meta?.hostId) {
    return normalizeHost(map[ticketGroupKey(String(meta.hostId))])
  }

  if (meta?.onModel === 'TicketAccount' && meta?.hostId) {
    return normalizeHost(map[ticketAccountKey(String(meta.hostId))])
  }

  return null
}

/**
 * Collect host keys that still need workflow trees after merging a list map.
 *
 * @param {object[]} tickets
 * @param {Record<string, object>} map
 * @param {object|null|undefined} user
 * @returns {string[]}
 */
function missingHostKeys(tickets, map, user) {
  const needed = new Set()

  for (const ticket of tickets) {
    const key = resolveSettingsHostKey(ticket, user)
    if (key && !resolveHost(map, key)) needed.add(key)
  }

  return [...needed]
}

class TicketSettingsHostUtility {
  slimFromSource(source) {
    return slimFromSource(source)
  }

  normalize(host) {
    return normalizeHost(host)
  }

  resolveKey(ticket, user) {
    return resolveSettingsHostKey(ticket, user)
  }

  resolve(map, key) {
    return resolveHost(map, key)
  }

  missingKeys(tickets, map, user) {
    return missingHostKeys(tickets, map, user)
  }

  ticketGroupKey(groupId) {
    return ticketGroupKey(groupId)
  }

  ticketAccountKey(accountId) {
    return ticketAccountKey(accountId)
  }

  normalizeGroupId(ref) {
    return normalizeGroupId(ref)
  }

  findGroup(user, groupId) {
    return findTicketGroup(user, groupId)
  }

  groupExists(user, groupId) {
    return ticketGroupExists(user, groupId)
  }

  accountIdsInGroup(group) {
    return accountIdsInGroup(group)
  }

  accountGroupId(user, accountId) {
    return accountGroupId(user, accountId)
  }

  findGroupForAccount(user, accountId) {
    return findGroupForAccount(user, accountId)
  }

  sharedGroupIdForAccounts(user, accountIds) {
    return sharedGroupIdForAccounts(user, accountIds)
  }
}

export default new TicketSettingsHostUtility()
